import React, { memo, Fragment, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation } from "swiper";
import imgLogo from "../../../../assets/Icons/imdb-logo.svg";
import Translate from "../../../../Components/Multilanguage/Translate";
import { imageUrlCheck } from "../../../../utils/utils";

const OttHeroSlider = memo(({ data }) => {
  const projectInfo = useSelector((state) => state?.projectInfo?.value);
  const [mainSwiperInstance, setMainSwiperInstance] = useState(null);
  const [thumbSwiperInstance, setThumbSwiperInstance] = useState(null);
  const navigate = useNavigate();

  const replaceImage = (e) => {
    const placeholder = projectInfo?.projectConfig?.config?.PLACEHOLDER_IMAGE;
    e.target.src = placeholder;
  };

  const handleMainSlideChange = useCallback((swiper) => {
    if (thumbSwiperInstance && !thumbSwiperInstance.destroyed && !swiper.triggeredByThumb) {
      thumbSwiperInstance.triggeredByMain = true;
      thumbSwiperInstance.slideToLoop(swiper.realIndex);
      setTimeout(() => { if(thumbSwiperInstance) thumbSwiperInstance.triggeredByMain = false; }, 100);
    }
  }, [thumbSwiperInstance]);

  const handleThumbSlideChange = useCallback((swiper) => {
    if (mainSwiperInstance && !mainSwiperInstance.destroyed && !swiper.triggeredByMain) {
      mainSwiperInstance.triggeredByThumb = true;
      mainSwiperInstance.slideToLoop(swiper.realIndex);
      setTimeout(() => {
        if (mainSwiperInstance && !mainSwiperInstance.destroyed) {
          mainSwiperInstance.triggeredByThumb = false;
          mainSwiperInstance.autoplay?.start();
        }
      }, 100);
    }
  }, [mainSwiperInstance]);

  const handleThumbClick = useCallback((index) => {
    if (mainSwiperInstance && !mainSwiperInstance.destroyed) {
      mainSwiperInstance.autoplay?.stop();
      mainSwiperInstance.triggeredByThumb = true;
      mainSwiperInstance.slideToLoop(index);
      setTimeout(() => { if(mainSwiperInstance) mainSwiperInstance.triggeredByThumb = false; }, 100);
    }
    if (thumbSwiperInstance && !thumbSwiperInstance.destroyed) {
       thumbSwiperInstance.slideToLoop(index);
    }
  }, [mainSwiperInstance, thumbSwiperInstance]);

  const showClickHandler = (item) => {
    if (item?.show_id) {
      navigate(`/show-details/${item?.vanity_url ? item?.vanity_url : item?.show_id}`, {
        state: { showId: item?.show_id },
      });
    } else if (item?.event_id) {
      navigate(`/event/${item?.vanity_url ? item?.vanity_url : item?.event_id}`, {
        state: { showId: item?.event_id },
      });
    }
  };

  return (
    <Fragment>
      <div className="ott-hero">
        <Swiper
          onSwiper={setMainSwiperInstance}
          slidesPerView={1}
          loop={true}
          autoplay={{ delay: 4000, disableOnInteraction: false }}
          modules={[Navigation, Autoplay]}
          navigation={true}
          className="landscape-swiper"
          onSlideChange={handleMainSlideChange}
          grabCursor={true}
          a11y={{
              prevSlideMessage: 'Previous slide',
              nextSlideMessage: 'Next slide',
          }}
        >
          {data.shows.map((item, index) => (
            <SwiperSlide key={item?.vanity_url || index} className="slide">
               <div className="background">
                 <img
                   className="bg-image"
                   src={
                     imageUrlCheck(item?.banner)
                       ? item.banner
                       : `${process.env.REACT_APP_IMAGE_URL || ''}${item?.banner}`
                   }
                   onError={replaceImage}
                   alt={item?.show_name ? `Background for ${item.show_name}` : 'Background image'}
                   loading="lazy"
                 />
                 <div className="overlay" />
               </div>
               <div className="left-wrapper">
                 <div className="left">
                   <div className="top">
                     {item?.rating && (
                       <span className="rating">{item.rating}</span>
                     )}
                     {item?.categories && item.categories.length > 0 && (
                       <ul className="genres">
                         {item.categories.map((category, i) => (
                           <li key={category?.key || i}> {category.category_name} </li>
                         ))}
                       </ul>
                     )}
                   </div>
                   {item?.show_name && <h1 className="title">{item.show_name}</h1>}
                   {item?.synopsis && <p className="description">{item.synopsis}</p>}
                   <div className="metadata">
                     {item?.imdb_rating && ( <div className="imdb"> <i className="fa fa-star text-warning" aria-hidden="true" /> <span>{item.imdb_rating}</span> <img src={imgLogo} alt="IMDB Logo" /> </div> )}
                     {item?.duration_text && ( <span>{item.duration_text}</span> )}
                     {item?.director && ( <span><Translate textKey="director" />: {item.director}</span> )}
                     {item?.show_cast && ( <span><Translate textKey="cast" />: {item.show_cast}</span> )}
                   </div>
                   {item?.vanity_url && <div className="watch"> <div onClick={()=>showClickHandler(item)}> <span><Translate textKey="play_now" /></span> <i className="fa-solid fa-play" aria-hidden="true" /> </div> </div> }
                 </div>
               </div>
            </SwiperSlide>
          ))}
        </Swiper>

        <div className="right-wrapper">
          <div className="right">
            <Swiper
              onSwiper={setThumbSwiperInstance}
              slidesPerView={2}
              spaceBetween={40}
              modules={[Navigation]}
              navigation={true}
              loop={true}
              className="portrait-swiper"
              onSlideChange={handleThumbSlideChange}
              watchSlidesProgress={true}
            >
              {data.shows.map((thumb, idx) => (
                <SwiperSlide
                    key={thumb?.vanity_url || idx}
                    className="slide"
                    onClick={() => handleThumbClick(idx)}
                    style={{ cursor: 'pointer' }}
                >
                    <img
                      src={
                        imageUrlCheck(thumb?.banner_portrait)
                          ? thumb.banner_portrait
                          : `${process.env.REACT_APP_IMAGE_URL || ''}${thumb?.banner_portrait}`
                      }
                      onError={replaceImage}
                      alt={thumb?.show_name ? `Thumbnail for ${thumb.show_name}` : 'Show thumbnail'}
                      loading="lazy"
                    />
                    <div className="description">
                      {thumb?.show_name && <h6>{thumb.show_name}</h6>}
                      {thumb?.duration_text && <span>{thumb.duration_text}</span>}
                    </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </div>
      </div>
    </Fragment>
  );
});

OttHeroSlider.displayName = "OttHeroSlider";
export default OttHeroSlider;